import React, { useState, useEffect, useRef, useCallback } from "react";
import Home from "../Home/Home";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RiDeleteBack2Line } from "react-icons/ri";
import DeleteDialog from "../../components/Reusable/DeleteDialogGeneric";
import { addAsset, getAssets, deleteAsset } from "../../Redux/Actions/asset";
import { Grid, Tooltip, LinearProgress, TextField, Slider, Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ImageIcon from "@material-ui/icons/Image";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import CropIcon from "@material-ui/icons/Crop";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { Storage } from "aws-amplify";
import { localStoragePut } from "../../util/localStorage";
import "./style.css";

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const storagePut = (key: string, file: any, opts: any): Promise<any> =>
  isLocalAuth ? localStoragePut(key, file, opts) : Storage.put(key, file, opts);

interface CroppedArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

function getCroppedImg(imageSrc: string, pixelCrop: CroppedArea, fileName: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("Canvas context error")); return; }
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error("Crop failed")); return; }
        const file = new File([blob], fileName, { type: "image/png" });
        resolve(file);
      }, "image/png", 1);
    };
    image.onerror = () => reject(new Error("Image load failed"));
    image.src = imageSrc;
  });
}

interface IProps {
  assets: any[];
  getAssets: () => void;
  addAsset: (asset: any) => void;
  deleteAsset: (assetId: any) => void;
  isDeletingAsset: boolean;
  loadingAssets: boolean;
}

const AssetsLibrary: React.FC<IProps> = ({
  assets,
  getAssets,
  addAsset,
  deleteAsset,
  isDeletingAsset,
  loadingAssets,
}) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assetName, setAssetName] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CroppedArea | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const allAssets = assets || [];

  useEffect(() => {
    getAssets();
  }, []);

  // Cleanup object URL on unmount / file change
  useEffect(() => {
    return () => { if (imageUrl) URL.revokeObjectURL(imageUrl); };
  }, [imageUrl]);

  const onCropComplete = useCallback((_: any, croppedPixels: CroppedArea) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(jpg|jpeg|png|gif|svg|webp)$/i)) {
      toast.error("Please select a valid image file (jpg, png, gif, svg, webp)");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setPendingFile(file);
    setImageUrl(URL.createObjectURL(file));
    setAssetName(file.name.replace(/\.[^/.]+$/, ""));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setDialogOpen(true);
  };

  const handleUploadConfirm = async () => {
    if (!pendingFile || !imageUrl) return;
    if (!assetName.trim()) {
      toast.error("Please enter a name for your asset");
      return;
    }

    setDialogOpen(false);
    setUploading(true);

    try {
      let fileToUpload: File = pendingFile;

      // If user cropped, generate the cropped file
      if (croppedAreaPixels) {
        fileToUpload = await getCroppedImg(
          imageUrl,
          croppedAreaPixels,
          `${assetName.trim()}.png`
        );
      }

      const key = `${uuid()}-asset`;
      const response: any = await storagePut(key, fileToUpload, {
        level: "protected",
        contentType: fileToUpload.type,
      });
      addAsset({ type: "logo", url: response.key, title: assetName.trim() });
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
      resetDialog();
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    resetDialog();
  };

  const resetDialog = () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setPendingFile(null);
    setImageUrl(null);
    setAssetName("");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Home>
      <div className="assetLibContainer">
        <div className="assetLibHeader">
          <div className="assetLibHeaderLeft">
            <button className="assetLibBackBtn" onClick={() => navigate("/")}>
              <ArrowBackIcon style={{ fontSize: 18 }} />
              Back
            </button>
            <h3 className="assetLibTitle">Asset Library</h3>
          </div>
          <button
            className="assetLibUploadBtn"
            onClick={handleUploadClick}
            disabled={uploading}
          >
            <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
            {uploading ? "Uploading..." : "Upload Asset"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>

        {uploading && <LinearProgress style={{ marginBottom: 16 }} />}

        {/* Crop & Name Dialog */}
        {dialogOpen && (
          <div className="assetCropOverlay">
            <div className="assetCropModal">
              <div className="assetCropModalHeader">
                <CropIcon style={{ fontSize: 20, color: "#fdb415" }} />
                <span>Crop & Name Your Asset</span>
              </div>

              <div className="assetCropArea">
                {imageUrl && (
                  <Cropper
                    image={imageUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={undefined}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    showGrid={true}
                    style={{
                      containerStyle: { width: "100%", height: "100%", borderRadius: 8 },
                    }}
                  />
                )}
              </div>

              <div className="assetCropControls">
                <div className="assetCropSliderRow">
                  <ZoomInIcon style={{ fontSize: 18, color: "#888", marginRight: 8 }} />
                  <span className="assetCropSliderLabel">Zoom</span>
                  <Slider
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.05}
                    onChange={(_, val) => setZoom(val as number)}
                    style={{ color: "#fdb415", flex: 1, marginLeft: 12 }}
                  />
                  <span className="assetCropZoomValue">{Math.round(zoom * 100)}%</span>
                </div>

                <TextField
                  fullWidth
                  label="Asset Name"
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleUploadConfirm(); }}
                  variant="outlined"
                  size="small"
                  style={{ marginTop: 12 }}
                />
              </div>

              <div className="assetCropActions">
                <button className="assetCropCancelBtn" onClick={handleCancel}>
                  Cancel
                </button>
                <button className="assetCropUploadBtn" onClick={handleUploadConfirm}>
                  Upload
                </button>
              </div>
            </div>
          </div>
        )}

        {loadingAssets ? (
          <div className="assetLibLoading">
            <LinearProgress style={{ width: "50%" }} />
            <p>Loading assets...</p>
          </div>
        ) : allAssets.length === 0 ? (
          <div className="assetLibEmpty">
            <PhotoLibraryIcon style={{ fontSize: 64, color: "#ccc" }} />
            <h3>No assets yet</h3>
            <p>Upload images and logos to use in your videos and email templates.</p>
            <button
              className="assetLibUploadBtn"
              onClick={handleUploadClick}
              disabled={uploading}
            >
              <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
              Upload Your First Asset
            </button>
          </div>
        ) : (
          <Grid container spacing={2} className="assetLibGrid">
            {allAssets.map((asset: any) => (
              <Grid item xs={6} sm={4} md={3} lg={3} key={asset._id}>
                <AssetCard
                  asset={asset}
                  deleteAsset={deleteAsset}
                  isDeletingAsset={isDeletingAsset}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    </Home>
  );
};

const AssetCard = ({ asset, deleteAsset, isDeletingAsset }: any) => {
  const [open, setOpen] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const handleDelete = () => {
    deleteAsset(asset._id);
  };

  return (
    <div
      className="assetLibCard"
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      <DeleteDialog
        open={open}
        closeDeleteDialog={() => setOpen(false)}
        actionDelete={handleDelete}
        isDeleting={isDeletingAsset}
        textContent="You will not be able to recover this asset once it is deleted"
      />
      <div className="assetLibCardImage">
        {asset.signedUrl ? (
          <img
            src={asset.signedUrl}
            crossOrigin="anonymous"
            className="assetLibImg"
            alt={asset.title || "asset"}
          />
        ) : (
          <ImageIcon style={{ fontSize: 48, color: "#ccc" }} />
        )}
      </div>
      <div className="assetLibCardFooter">
        <span className="assetLibName">{asset.title || "Untitled"}</span>
      </div>
      {showDelete && (
        <button className="assetLibDeleteBtn" onClick={() => setOpen(true)}>
          <Tooltip placement="top" title="Delete" arrow>
            <span>
              <RiDeleteBack2Line size="1.3em" />
            </span>
          </Tooltip>
        </button>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  assets: state.asset.assets,
  isDeletingAsset: state.asset.isDeletingAsset,
  loadingAssets: state.asset.loadingAssets,
});

const mapDispatchToProps = (dispatch: any) => ({
  getAssets: () => dispatch(getAssets()),
  addAsset: (asset: any) => dispatch(addAsset(asset)),
  deleteAsset: (assetId: any) => dispatch(deleteAsset(assetId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssetsLibrary);
